import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useCallback, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { generateGuid } from 'utils/guid';
import {
  DeviceCredentialsDialog,
  type DeviceCredentialsFormValues,
} from './DeviceCredentialsDialog';
import {
  type DeviceCredential,
  DevicePasswordsList,
} from './DevicePasswordsList';

type Props = {
  credentials: DeviceCredential[];
  onCredentialsChange: (credentials: DeviceCredential[]) => void;
  noCredentialBox: JSX.Element;
  renderTitleBadge?: () => ReactNode;
};

const buildBaseCredentialData = (values: DeviceCredentialsFormValues) => {
  const isPasswordAuth = values.authType === 'password';
  return {
    username: values.username,
    network: values.network.ips,
    excludeNetworks: values.excludeNetworks.ips,
    notes: values.notes,
    syslog: values.syslog,
    expirationDate: {
      enabled: values.expirationEnabled,
      value:
        values.expirationEnabled && values.expirationValue
          ? values.expirationValue.toISOString()
          : undefined,
    },
    // Only include password or SSH fields based on authType — never both
    password: isPasswordAuth ? values.password || null : null,
    privateKey: isPasswordAuth ? null : values.privateKey || null,
    passphrase: isPasswordAuth
      ? null
      : values.passphraseEnabled
        ? values.passphrase || null
        : null,
  };
};

type BaseCredentialData = ReturnType<typeof buildBaseCredentialData>;

const applyEditsToCredential = (
  existing: DeviceCredential,
  values: DeviceCredentialsFormValues,
  credentialData: BaseCredentialData,
): DeviceCredential => {
  const isPasswordAuth = values.authType === 'password';
  const mergedPassphrase = isPasswordAuth
    ? null
    : values.passphraseEnabled
      ? credentialData.passphrase || existing.passphrase || null
      : null;
  return {
    ...existing,
    ...credentialData,
    // password auth: keep existing if no new password entered; SSH auth: always null
    password: isPasswordAuth
      ? credentialData.password || existing.password
      : null,
    // when the password is changed, mark the credential as custom to encrypt password
    ...(credentialData.password ? { custom: true } : {}),
    // user should not be able to change username in edit mode
    username: existing.username ?? '',
    // SSH auth: keep existing key if no new key entered; password auth: always null
    privateKey: isPasswordAuth
      ? null
      : credentialData.privateKey || existing.privateKey || null,
    // SSH auth: keep existing passphrase if no new passphrase entered; password auth: always null
    passphrase: mergedPassphrase,
  };
};

export const DeviceCredentials = (props: Props) => {
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const [editedCredential, setEditedCredential] = useState<
    DeviceCredential | 'new' | null
  >(null);

  const getInitialValues = useCallback((): DeviceCredentialsFormValues => {
    if (editedCredential === 'new') {
      return {
        authType: 'password',
        username: '',
        password: '',
        retypePassword: '',
        expirationEnabled: false,
        expirationValue: null,
        network: { ips: ['0.0.0.0/0', '::/0'] },
        excludeNetworks: { ips: [] },
        notes: '',
        syslog: true,
        privateKey: '',
        passphraseEnabled: false,
        passphrase: '',
      };
    }

    if (editedCredential) {
      return {
        authType: editedCredential.privateKey ? 'ssh' : 'password',
        username: editedCredential.username ?? '',
        password: '',
        retypePassword: '',
        expirationEnabled: editedCredential.expirationDate?.enabled ?? false,
        expirationValue: editedCredential.expirationDate?.value
          ? new Date(editedCredential.expirationDate.value)
          : null,
        network: { ips: editedCredential.network ?? [] },
        excludeNetworks: { ips: editedCredential.excludeNetworks ?? [] },
        notes: editedCredential.notes ?? '',
        syslog: editedCredential.syslog,
        privateKey: '',
        passphraseEnabled: !!editedCredential.passphrase,
        passphrase: '',
      };
    }

    stumble(
      'warning: edit/add dialog was invoked with `null` editedCredential, that is impossible',
    );
    return {
      authType: 'password',
      username: '',
      password: '',
      retypePassword: '',
      expirationEnabled: false,
      expirationValue: null,
      network: { ips: [] },
      excludeNetworks: { ips: [] },
      notes: '',
      syslog: true,
      privateKey: '',
      passphraseEnabled: false,
      passphrase: '',
    };
  }, [editedCredential]);

  const handleSaveCredential = useStableCallback(
    (values: DeviceCredentialsFormValues) => {
      const credentialData = buildBaseCredentialData(values);

      if (editedCredential === 'new') {
        const maxPriority = Math.max(
          0,
          ...props.credentials.map((c) => c.priority),
        );
        props.onCredentialsChange([
          ...props.credentials,
          {
            ...credentialData,
            id: generateGuid(),
            priority: maxPriority + 1,
            // encrypt password for new credential
            custom: true,
          },
        ]);
      } else if (editedCredential) {
        props.onCredentialsChange(
          props.credentials.map((c) =>
            c.id === editedCredential.id
              ? applyEditsToCredential(c, values, credentialData)
              : c,
          ),
        );
      }

      setEditedCredential(null);
    },
  );

  const handleEditCredential = useCallback((credential: DeviceCredential) => {
    return () => {
      setEditedCredential(credential);
    };
  }, []);

  const handleDeleteCredential = useStableCallback((id: string) => {
    return async () => {
      if (
        await confirmDeleteDialog.show(
          {
            header: 'Delete credential',
            subHeader: 'Are you sure you want to delete this credential?',
            btnConfirm: 'Delete',
            btnSecondaryConfirm: 'Cancel',
          },
          {
            btnConfirmDataTestId: 'credentials-dialog-remove-confirm',
          },
        )
      ) {
        props.onCredentialsChange(props.credentials.filter((c) => c.id !== id));
      }
    };
  });

  const handleReorder = useStableCallback(
    (_data: DeviceCredential[], draggedIndex: number, targetIndex: number) => {
      const result = [...props.credentials];
      const [removed] = result.splice(draggedIndex, 1);
      if (removed) {
        result.splice(targetIndex, 0, removed);
      }
      props.onCredentialsChange(
        result.map((c, idx) => ({ ...c, priority: idx + 1 })),
      );
    },
  );

  return (
    <>
      <DevicePasswordsList
        className='-mx -mb -mt-4 rounded-b-lg bg-transparent flex-1'
        passwords={props.credentials}
        onAddCredentialClick={() => setEditedCredential('new')}
        onDropPassword={handleReorder}
        onEditPasswordClick={handleEditCredential}
        onDeletePasswordClick={handleDeleteCredential}
        noCredentialBox={props.noCredentialBox}
        titleEndAdornment={props.renderTitleBadge?.()}
      />

      {editedCredential !== null && (
        <DeviceCredentialsDialog
          initialValues={getInitialValues()}
          onClose={() => setEditedCredential(null)}
          onSubmit={handleSaveCredential}
          formType={editedCredential === 'new' ? 'add' : 'edit'}
          hasExistingPrivateKey={
            editedCredential !== 'new' && !!editedCredential.privateKey
          }
          hasExistingPassword={
            editedCredential !== 'new' && !!editedCredential.password
          }
        />
      )}

      {confirmDeleteDialog.render()}
    </>
  );
};
